import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import authService from "../services/authService";
import { registerAuthSessionHandlers } from "../services/api";
import {
  VAULT_SESSION,
  clearVaultSession,
  isVaultPath,
  loadVaultSession,
  markVaultSessionExpired,
  saveVaultSession,
  updateVaultSessionAccessToken,
} from "../utils/vaultSession";

const AuthContext = createContext();

// Upper bound on the "Signing you in…" screen, in case the dashboard never
// reports its first data (chunk load failure, a hung request) — the user then
// sees whatever the page has instead of a spinner forever.
const SIGN_IN_OVERLAY_MAX_MS = 20000;

// The backend redirects back to "/?sso_token=...". Knowing that on the very
// first render lets the route guards wait instead of bouncing to /login while
// useSsoCallback hasn't run yet.
const hasSsoTokenInUrl = () =>
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("sso_token");

// A vault session saved by an earlier QR scan, picked up when this page load
// is itself a vault page (see utils/vaultSession.js). Never anywhere else.
const restorableVaultSession = () =>
  typeof window !== "undefined" &&
  !hasSsoTokenInUrl() &&
  isVaultPath(window.location.pathname)
    ? loadVaultSession()
    : null;

const normalizeUser = (nextUser) => {
  if (!nextUser) return nextUser;
  return {
    ...nextUser,
    role: nextUser.is_superadmin ? "superadmin" : nextUser.role,
    teams: nextUser.teams || [],
    status: nextUser.status || "Active",
  };
};

/**
 * AuthProvider
 *
 * Purpose: Provides authentication state and auth method signatures.
 * Sign-in is SSO-only (Microsoft or Google) — see loginWithSsoTokens, which
 * the App-level SSO callback effect calls once it reads sso_token/sso_refresh
 * out of the query string App.js gets redirected to.
 * Used by: App, route guards, auth-aware components.
 */
export function AuthProvider({ children }) {
  const [restoredSession] = useState(restorableVaultSession);
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(restoredSession?.accessToken ?? null);
  const [refreshToken, setRefreshToken] = useState(restoredSession?.refreshToken ?? null);
  // Set only for a vault session (signed in from a vault QR): when it ends
  // and the user has to sign in again. Null for the normal session.
  const [sessionExpiresAt, setSessionExpiresAt] = useState(
    restoredSession?.expiresAt ?? null,
  );
  const [loading, setLoading] = useState(() => hasSsoTokenInUrl() || Boolean(restoredSession));
  // True from the SSO redirect back until the session is verified AND the
  // dashboard has its first data (it calls finishSignIn) — drives the
  // "Signing you in…" screen so there's no login flash, blank page or
  // 0-value dashboard in between.
  const [signingIn, setSigningIn] = useState(hasSsoTokenInUrl);
  const [error, setError] = useState(null);

  const finishSignIn = useCallback(() => setSigningIn(false), []);

  useEffect(() => {
    if (!signingIn) return undefined;
    const timer = setTimeout(finishSignIn, SIGN_IN_OVERLAY_MAX_MS);
    return () => clearTimeout(timer);
  }, [signingIn, finishSignIn]);

  const clearAuth = useCallback(() => {
    clearVaultSession();
    setSessionExpiresAt(null);
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    setError(null);
    setLoading(false);
    setSigningIn(false);
  }, []);

  const persistAuth = useCallback((tokens, nextUser) => {
    if (tokens.access_token) {
      setAccessToken(tokens.access_token);
    }
    if (tokens.refresh_token) {
      setRefreshToken(tokens.refresh_token);
    }
    if (nextUser) {
      const normalized = normalizeUser(nextUser);
      setUser(normalized);
    }
  }, []);

  // A vault session ran out: back to /login (via ProtectedRoute, which
  // remembers the vault), with a note saying why.
  const expireVaultSession = useCallback(() => {
    markVaultSessionExpired();
    clearAuth();
  }, [clearAuth]);

  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) {
      clearAuth();
      return null;
    }
    try {
      const response = await authService.refresh(refreshToken);
      setAccessToken(response.access_token);
      if (sessionExpiresAt) updateVaultSessionAccessToken(response.access_token);
      setError(null);
      return response.access_token;
    } catch (err) {
      if (sessionExpiresAt) markVaultSessionExpired();
      clearAuth();
      throw err;
    }
  }, [clearAuth, refreshToken, sessionExpiresAt]);

  const loginWithSsoTokens = useCallback(
    async ({ accessToken: nextAccessToken, refreshToken: nextRefreshToken, session }) => {
      setError(null);
      setLoading(true);
      setSigningIn(true);
      // Synchronously register the access token before the getMe() call below
      // to avoid the 401 race condition in the axios interceptor.
      registerAuthSessionHandlers({
        getAccessToken: () => nextAccessToken,
        refreshAccessToken,
        clearAuth,
      });
      persistAuth(
        { access_token: nextAccessToken, refresh_token: nextRefreshToken },
        null,
      );
      // Signed in from a vault QR: keep it for the next scan, until it ends.
      setSessionExpiresAt(
        session === VAULT_SESSION
          ? saveVaultSession({ accessToken: nextAccessToken, refreshToken: nextRefreshToken })
          : null,
      );
      try {
        const nextUser = await authService.getMe();
        const normalized = normalizeUser(nextUser);
        setUser(normalized);
        setLoading(false);
        // A Pending user gets PendingActivationPage, not the dashboard, so
        // there's no first data to wait for.
        if (normalized.status === "Pending") setSigningIn(false);
        return normalized;
      } catch (err) {
        clearAuth();
        throw err;
      }
    },
    [persistAuth, refreshAccessToken, clearAuth],
  );

  const logout = useCallback(async () => {
    try {
      if (refreshToken) await authService.logout(refreshToken);
    } catch (_error) {
      // Local logout must still complete if server revocation fails.
    } finally {
      clearAuth();
    }
  }, [clearAuth, refreshToken]);

  useEffect(() => {
    registerAuthSessionHandlers({
      getAccessToken: () => accessToken,
      refreshAccessToken,
      clearAuth,
    });

    return () => {
      registerAuthSessionHandlers();
    };
  }, [accessToken, clearAuth, refreshAccessToken]);

  // A restored vault session has tokens but no user yet — fetch it before
  // the route guards let the vault page render. A dead token goes through
  // the api interceptor's refresh, which signs out if that fails too.
  useEffect(() => {
    if (!restoredSession) return;
    authService
      .getMe()
      .then((nextUser) => setUser(normalizeUser(nextUser)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [restoredSession]);

  // End a vault session on the dot. Phones suspend timers in background
  // tabs, so also check whenever the page comes back into view.
  useEffect(() => {
    if (!sessionExpiresAt) return undefined;
    const checkExpiry = () => {
      if (Date.now() >= sessionExpiresAt) expireVaultSession();
    };
    const timer = setTimeout(checkExpiry, Math.max(0, sessionExpiresAt - Date.now()));
    document.addEventListener("visibilitychange", checkExpiry);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", checkExpiry);
    };
  }, [sessionExpiresAt, expireVaultSession]);

  // Keep `user` in sync with the server whenever the app regains focus or the
  // status might have changed server-side (e.g. an admin just approved this
  // Pending account) — PendingActivationPage polls this explicitly too.
  const refreshMe = useCallback(async () => {
    if (!accessToken) return null;
    const nextUser = await authService.getMe();
    const normalized = normalizeUser(nextUser);
    setUser(normalized);
    return normalized;
  }, [accessToken]);

  const isAuthenticated = Boolean(accessToken);
  const isPending = user?.status === "Pending";
  const isSuperAdmin = useCallback(
    () => user?.role === "superadmin" || Boolean(user?.is_superadmin),
    [user],
  );
  const isSuperAdminOrManager = useCallback(
    () =>
      isSuperAdmin() ||
      user?.role === "manager" ||
      user?.teams?.some((team) => team.role === "manager"),
    [isSuperAdmin, user],
  );
  const canUseExpenseImport = useCallback(
    () => isSuperAdmin() || Boolean(user?.can_use_expense_import),
    [isSuperAdmin, user],
  );
  const canViewExpenseRecord = useCallback(
    () => isSuperAdmin() || Boolean(user?.teams?.some((team) => team.can_view_expense_record)),
    [isSuperAdmin, user],
  );

  const value = useMemo(
    () => ({
      user,
      accessToken,
      refreshToken,
      sessionExpiresAt,
      isAuthenticated,
      isPending,
      loading,
      signingIn,
      finishSignIn,
      error,
      clearAuth,
      loginWithSsoTokens,
      logout,
      refreshAccessToken,
      refreshMe,
      isSuperAdmin,
      isSuperAdminOrManager,
      canUseExpenseImport,
      canViewExpenseRecord,
    }),
    [
      user,
      accessToken,
      refreshToken,
      sessionExpiresAt,
      isAuthenticated,
      isPending,
      loading,
      signingIn,
      finishSignIn,
      error,
      clearAuth,
      loginWithSsoTokens,
      logout,
      refreshAccessToken,
      refreshMe,
      isSuperAdmin,
      isSuperAdminOrManager,
      canUseExpenseImport,
      canViewExpenseRecord,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }

  return context;
};

export default AuthContext;
